import React, { useEffect, useMemo, useState } from 'react'

import { useTranslation } from 'react-i18next'
import {
  useFirmSelectionActions,
  useAllSelections
} from '../../../../../store/firmSelectionStore'

import Step from '@mui/material/Step'
import StepLabel from '@mui/material/StepLabel'
import Typography from '@mui/material/Typography'
import Divider from '@mui/material/Divider'
import MuiStepper from '@mui/material/Stepper'
import Box from '@mui/material/Box'
import styled from '@mui/material/styles/styled'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import { Button } from '@mui/material'

import StepperWrapper from '../atoms/StepperWrapper'
import FirmListManager from '../organisms/FirmListManager'
import FirmSelectionConfirmDialog from '../molecules/FirmSelectionConfirmDialog'

import {
  getFirms,
  getTemplates,
  selectFirms
} from '../../../services/B2BServices'
import { getUrlParams } from '../../../services/urlServices'
import { useToast } from '../../../../../campaigns/hooks/useToast'
import { useEndpointStatusActions } from '../../../../../store/endpointStatus'
import { sortTregFirms } from '../../../../../pages/mc-components/McPageHelpers'

const Stepper = styled(MuiStepper)(({ theme }) => ({
  justifyContent: 'center',
  '& .MuiStep-root': {
    '& + i': {
      display: 'none',
      color: 'var(--mui-palette-text-secondary) !important'
    },
    '& .MuiStepLabel-label': {
      display: 'flex',
      cursor: 'pointer',
      alignItems: 'center',
      paddingBlock: theme.spacing(5),
      svg: {
        marginInline: theme.spacing(1.5),
        fill: 'var(--mui-palette-text-primary)'
      },
      '&.Mui-active, &.Mui-completed': {
        '& .MuiTypography-root': { color: 'var(--mui-palette-primary-main)' },
        '& svg': { fill: 'var(--mui-palette-primary-main)' }
      }
    },
    '&.Mui-completed + i': { color: 'var(--mui-palette-primary-main) !important' },
    [theme.breakpoints.up('md')]: {
      paddingBottom: 0,
      '& + svg': { display: 'block' },
      '& .MuiStepLabel-label': { display: 'block' }
    }
  }
}))

const getStepContent = (
  step,
  firmState,
  sectorState,
  paginationState,
  emailState,
  uiConfig,
  isServerSide
) => {
  switch (step) {
    case 0:
      return (
        <FirmListManager
          firmState={firmState}
          sectorState={sectorState}
          paginationState={paginationState}
          emailState={emailState}
          uiConfig={uiConfig}
          isServerSide={isServerSide}
        />
      )
    default:
      return null
  }
}

const Wizard = ({ onCampaignUpdate }) => {
  const { t } = useTranslation()
  const queryParams = getUrlParams()
  const { showSuccess, showError } = useToast()

  const allSelections = useAllSelections()

  const { resetSelection } = useFirmSelectionActions()
  const { setRetryCallback } = useEndpointStatusActions()

  const [activeStep] = useState(0)
  const [nextLoading, setNextLoading] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState(null)
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [firmState, setFirmState] = useState({
    rowSelection: {},
    b2bFirms: {},
    tradeRegistryFirmKeys: [],
    selectedFirms: {},
    fetching: true,
    excelFirms: [],
    activeFirmKey: null,
    filteredRegistryFirmsData: {}
  })

  const [sectorState, setSectorState] = useState({
    tradeRegistrySectors: {},
    filteredSectors: {},
    selectedTradeRegistrySectors: {}
  })

  const [paginationState, setPaginationState] = useState({
    serverSidePagination: {}
  })

  const [emailState, setEmailState] = useState({
    additionalEmails: []
  })

  const isAnyFirmSelected = useMemo(() => {
    return Object.values(allSelections).some(selectionData =>
      selectionData.mode === 'all' || selectionData.items?.length > 0
    )
  }, [allSelections])

  const updateFirmState = (updates) => setFirmState(
    prev => ({ ...prev, ...updates }))
  const updateSectorState = (updates) => setSectorState(
    prev => ({ ...prev, ...updates }))
  const updatePaginationState = (updates) => setPaginationState(
    prev => ({ ...prev, ...updates }))
  const updateEmailState = (updates) => setEmailState(
    prev => ({ ...prev, ...updates }))

  const setFilteredSectorsByKey = (key, val) => {
    updateSectorState(
      { filteredSectors: { ...sectorState.filteredSectors, [key]: val } })
  }

  const firmKeys = useMemo(() => {
    const b2bKeys = Object.keys(firmState.b2bFirms)
    const tradeRegistryKeys = firmState.tradeRegistryFirmKeys || []

    const orderedKeys = []
    if (tradeRegistryKeys.length > 0) {
      orderedKeys.push(...tradeRegistryKeys)
    }
    if (b2bKeys.includes('spFirms')) {
      orderedKeys.push('spFirms')
    }
    if (b2bKeys.includes('rfFirms')) {
      orderedKeys.push('rfFirms')
    }

    return orderedKeys
  }, [
    sectorState.tradeRegistrySectors,
    firmState.b2bFirms,
    firmState.tradeRegistryFirmKeys])

  const steps = [
    { title: t('mailTemplate.wizard.firmSelection'), icon: AddBusinessIcon }
  ]

  const handleSelectFirm = () => {
    if (!isAnyFirmSelected && !emailState.additionalEmails?.length &&
      !firmState.excelFirms?.length) {
      showError(t('mailTemplate.wizard.firmError'))
      return
    }
    setConfirmDialogOpen(true)
  }

  const handleCancelFirmSelection = () => setConfirmDialogOpen(false)

  const transformSelectedFirms = () => {
    const transformedSelectedFirms = {}
    const tradeRegistryFirms = {}

    const getFirmData = (firmObject) => {
      if (firmObject.firmType === 'P7' && firmObject.p7Firm) {
        return firmObject.p7Firm
      }
      if (firmObject.firmType === 'TREG' && firmObject.tregFirm) {
        return firmObject.tregFirm
      }
      return firmObject
    }

    Object.entries(allSelections).forEach(([firmKey, selectionData]) => {
      const { mode, items } = selectionData
      const isTradeRegistryFirm = firmState.tradeRegistryFirmKeys.includes(
        firmKey)

      if (isTradeRegistryFirm) {
        const sectors = sectorState.selectedTradeRegistrySectors[firmKey]?.map(
          item => item.code) || []
        const firmMap = new Map()
        items.forEach(firmObject => {
          const firmData = getFirmData(firmObject)
          const identifier = firmData.companyName || firmData.opensearchId

          if (identifier) {
            firmMap.set(identifier, {
              firmType: firmObject.firmType || 'TREG',
              tregFirm: {
                opensearchId: firmData.opensearchId
              }
            })
          }
        })
        tradeRegistryFirms[firmKey] = {
          mode,
          items: Array.from(firmMap.values()),
          sectors
        }
      } else {
        const firmMap = new Map()
        items.forEach(firmObject => {
          const firmData = getFirmData(firmObject)
          const identifier = firmData.name || firmData.url_code

          if (identifier) {
            firmMap.set(identifier, {
              firmType: firmObject.firmType || 'P7',
              p7Firm: {
                url_code: firmData.url_code
              }
            })
          }
        })
        transformedSelectedFirms[firmKey] = {
          mode,
          items: Array.from(firmMap.values())
        }
      }
    })

    return { transformedSelectedFirms, tradeRegistryFirms }
  }

  const handleConfirmFirmSelection = (forcedFirmLimit = null) => {
    setConfirmDialogOpen(false)
    setNextLoading(true)

    const additionalEmailsArr = emailState.additionalEmails?.length
      ? [{
          firmType: 'ADDITIONAL',
          additionalFirm: {
            emailList: emailState.additionalEmails.join(';'),
            name: null,
            website: null
          }
        }]
      : []

    const excelFirmsFormatted = firmState.excelFirms.map(firm => ({
      firmType: 'ADDITIONAL',
      additionalFirm: {
        name: firm.name,
        emailList: firm.emailList?.join(';') || '',
        website: firm.website?.join(';') || ''
      }
    }))

    const additionalFirmList = additionalEmailsArr.concat(excelFirmsFormatted)
    const {
      transformedSelectedFirms,
      tradeRegistryFirms
    } = transformSelectedFirms()

    const payload = {
      b2bCampaignId: queryParams.b2bCampaignId,
      templateId: selectedTemplate?.id,
      templateParameters: {},
      additionalFirmList,
      ...transformedSelectedFirms,
      ...(Object.keys(tradeRegistryFirms).length > 0 && { tradeRegistryFirms }),
      ...(forcedFirmLimit !== null && { forcedFirmLimit })
    }

    setRetryCallback((retryForcedLimit) => {
      handleConfirmFirmSelection(retryForcedLimit)
    })

    selectFirms(payload).then(async () => {
      showSuccess(t('mailTemplate.wizard.firmSelectionSuccess'))
      resetSelection()
      setRetryCallback(null)
      if (onCampaignUpdate) await onCampaignUpdate()
    }).catch((err) => {
      showError(err || t('mailTemplate.wizard.firmSelectionError'))
    }).finally(() => setNextLoading(false))
  }

  const setRowSelectionByKey = (key, callback) => {
    updateFirmState({
      rowSelection: {
        ...firmState.rowSelection,
        [key]: callback(firmState.rowSelection[key])
      }
    })
  }

  const setSelectedFirmsByKey = (key, val) => {
    if (key) {
      updateFirmState(
        { selectedFirms: { ...firmState.selectedFirms, [key]: val } })
    }
  }

  const setSelectedTradeRegistrySectorsByKey = (key, val) => {
    if (key) {
      updateSectorState({
        selectedTradeRegistrySectors: {
          ...sectorState.selectedTradeRegistrySectors,
          [key]: val
        }
      })
    }
  }

  useEffect(() => {
    if (!queryParams?.b2bCampaignId) return
    getFirms({ b2bCampaignId: queryParams.b2bCampaignId }).then((res) => {
      const tradeRegistryKeys = Object.keys(res.data?.tradeRegistrySectors || {})

      const initialActiveFirmKey = 'rfFirms'

      updateFirmState({
        b2bFirms: { rfFirms: res.data.rfFirms, spFirms: res.data.spFirms },
        tradeRegistryFirmKeys: tradeRegistryKeys,
        activeFirmKey: initialActiveFirmKey
      })
      updateSectorState(
        { tradeRegistrySectors: res.data?.tradeRegistrySectors })
    }).catch((error) => {
      if (error.response?.status === 404) {
        showError(
          t('mailTemplate.wizard.firmList.noData'))
      } else {
        showError(error.message ||
          t('mailTemplate.wizard.firmList.tradeRegistryFetchErr'))
      }
      updateFirmState(
        { b2bFirms: {}, tradeRegistryFirmKeys: [], activeFirmKey: null })
      updateSectorState({ tradeRegistrySectors: {} })
    }).finally(() => updateFirmState({ fetching: false }))
  }, [queryParams.b2bCampaignId, showError, t])

  const getTemplatesWithSelected = (selectedTemplateId = null) => {
    if (!queryParams?.b2bAccountId) return
    getTemplates({ b2bAccountId: queryParams.b2bAccountId }).then((res) => {
      const selected = selectedTemplateId
        ? res.data?.find(
          (x) => x.id === selectedTemplateId)
        : res.data?.[0]
      setSelectedTemplate(selected)
    })
  }

  useEffect(() => { getTemplatesWithSelected() }, [])
  useEffect(() => () => { resetSelection() }, [resetSelection])

  const queryParamsError = (
    queryParams, b2bAccountIdError, b2bCampaignIdError) => {
    const b2bAccountId = queryParams?.b2bAccountId
    const b2bCampaignId = queryParams?.b2bCampaignId
    if (b2bAccountId && b2bCampaignId) return false
    else if (b2bCampaignId) return <div>{b2bAccountIdError}</div>
    else return <div>{b2bCampaignIdError}</div>
  }

  const stepContent = useMemo(() => getStepContent(
    activeStep,
    {
      ...firmState,
      setFilteredRegistryFirmsData: (data) => updateFirmState(
        { filteredRegistryFirmsData: data }),
      setActiveFirmKey: (key) => updateFirmState({ activeFirmKey: key }),
      data: firmState.b2bFirms,
      setSelectedFirms: (val) => setSelectedFirmsByKey(firmState.activeFirmKey,
        val),
      rowSelection: firmState.rowSelection?.[firmState.activeFirmKey] || {},
      setRowSelection: (val) => setRowSelectionByKey(firmState.activeFirmKey,
        val),
      setExcelFirms: (firms) => updateFirmState({ excelFirms: firms }),
      firmKeys
    },
    {
      ...sectorState,
      setFilteredSectorsByKey: (val) => setFilteredSectorsByKey(
        firmState.activeFirmKey, val),
      selectedTradeRegistrySectors: sectorState.selectedTradeRegistrySectors?.[firmState.activeFirmKey] ||
        [],
      setSelectedTradeRegistrySectors: (val) => setSelectedTradeRegistrySectorsByKey(
        firmState.activeFirmKey, val),
      tradeRegistrySectors: sortTregFirms(sectorState.tradeRegistrySectors?.[firmState.activeFirmKey]) ||
        []
    },
    {
      ...paginationState,
      setServerSidePagination: (updater) => {
        if (typeof updater === 'function') {
          setPaginationState(prev => ({
            ...prev,
            serverSidePagination: updater(prev.serverSidePagination)
          }))
        } else {
          updatePaginationState({ serverSidePagination: updater })
        }
      }
    },
    {
      ...emailState,
      setAdditionalEmails: (emails) => updateEmailState(
        { additionalEmails: emails })
    },
    { hideTabs: false, hideActionButtons: false },
    firmState.tradeRegistryFirmKeys.includes(firmState.activeFirmKey)
  ), [
    activeStep,
    firmState.activeFirmKey,
    firmState.b2bFirms,
    firmState.filteredRegistryFirmsData,
    firmState.rowSelection,
    firmState.tradeRegistryFirmKeys,
    sectorState.selectedTradeRegistrySectors,
    sectorState.tradeRegistrySectors,
    paginationState.serverSidePagination,
    emailState.additionalEmails,
    firmKeys
  ])

  const headerContent = (
    <div className="p-2" style={{ direction: 'ltr' }}>
      <StepperWrapper className="mb-4">
        <Stepper
          activeStep={activeStep}
          connector={<ArrowForwardIosIcon
            className="mli-12 hidden md:block text-xl text-textDisabled"/>}
        >
          {steps.map((step, index) => (
            <Step key={index}>
              <StepLabel icon={<></>} className="text-center">
                <step.icon sx={{ fontSize: 50 }}/>
                <Typography className="step-title">{step.title}</Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </StepperWrapper>

      <div className="relative">
        <Button
          variant="contained"
          onClick={handleSelectFirm}
          disabled={nextLoading ||
            (!isAnyFirmSelected && !emailState.additionalEmails?.length &&
              !firmState.excelFirms?.length)}
          style={{ position: 'absolute', right: 0, bottom: 0 }}
          sx={{ borderRadius: 2 }}
        >
          {nextLoading
            ? t('common.loading')
            : t(
              'mailTemplate.wizard.selectFirm')}
        </Button>
      </div>
    </div>
  )

  return (
    queryParamsError(queryParams, t('mailTemplate.wizard.b2bAccountIdError'),
      t('mailTemplate.wizard.b2bCampaignIdError')) ||
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {headerContent}
      <Divider/>
      <Box sx={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        minHeight: '400px',
        position: 'relative'
      }}>
        <div
          style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <div style={{ flex: 1 }}>
            {stepContent}
          </div>
        </div>
      </Box>

      <FirmSelectionConfirmDialog
        open={confirmDialogOpen}
        onConfirm={() => handleConfirmFirmSelection()}
        onCancel={handleCancelFirmSelection}
      />
    </Box>
  )
}

export default Wizard
